import { nanoid } from 'nanoid'
import { isObject, isWithTopic } from './tools'

export const meta = ''

/**
 * According to https://zelark.github.io/nano-id-cc/
 * ID with length 10 has collision probability of 1% in a 152M IDs,
 * or ~2 days of continuous generation at 1K IDs/s,
 * I think it is acceptable for most messaging use cases.
 */
export const idLength = 10

export const EVENT = 1 << 0
export const REQUEST = 1 << 1
export const RESPONSE = 1 << 2
export const ERROR = 1 << 3
export const CLASS_BASED = 1 << 4
export const STOP_PROPAGATION = 1 << 5
export const STOP_IMMEDIATE_PROPAGATION = 1 << 6

export type MessageType = number

export type MessageTopic = string

export type MessageId = string

export type MessageTimestamp = number

export type MessageMeta = {
  readonly [meta]: Readonly<
    [MessageType, MessageTopic, MessageId, MessageTimestamp]
  >
}

type AnyCls = {
  new (...args: any[]): any
}

export type MessageCls<T extends AnyCls = AnyCls> = T & {
  readonly topic: string
}

function message(type: MessageType) {
  return (topic: string | undefined, payload: unknown, from?: MessageMeta) => {
    let extendedType = type
    let body: Record<string, unknown> | undefined

    if (isObject(payload)) {
      const Cls = payload.constructor

      // payload is a class instance - class MUST have static `topic` property
      if (
        Cls.name !== 'Object' &&
        Cls !== Object &&
        Object.getPrototypeOf(payload) === Cls.prototype &&
        isWithTopic(Cls)
      ) {
        topic = Cls.topic
        extendedType |= CLASS_BASED
      }

      // payload is an object - object MUST have `topic` property
      else if (isWithTopic(payload)) {
        topic = payload.topic
      }
    }

    if (topic == null) {
      throw new Error('Topic is not defined')
    }

    // if payload exists
    if (payload != null) {
      // if payload is an object, copy its properties to body
      if (
        isObject(payload) &&
        Object.prototype.toString.call(payload) === '[object Object]'
      ) {
        body = { ...payload }

        // and remove topic from body, if present
        if ('topic' in body) {
          delete body.topic
        }
      }

      // wrap instance of error into `{ error }`
      else if (payload instanceof Error) {
        body = { error: payload }
      }

      // otherwise, wrap payload into `{ payload }`
      else {
        body = { payload }
      }
    }

    // extract metadata from `from` message, if provided
    const [, fromTopic = '', fromId = ''] = from?.[meta] ?? []

    // return message envelope with metadata
    return {
      ...body,
      [meta]: [
        extendedType,
        fromTopic || topic,
        fromId || nanoid(idLength),
        performance.timeOrigin + performance.now(),
      ],
    } as MessageMeta
  }
}

export const createEvent = message(EVENT)
export const createRequest = message(REQUEST)
export const createResponse = message(RESPONSE)
export const createError = message(ERROR)

export function stopPropagation(message: MessageMeta): void {
  const metaRef = message[meta] as unknown as number[]
  metaRef[0] |= STOP_PROPAGATION
}

export function stopImmediatePropagation(message: MessageMeta): void {
  const metaRef = message[meta] as unknown as number[]
  metaRef[0] |= STOP_IMMEDIATE_PROPAGATION
}
